import { useState, useEffect } from 'react';
import { txApi } from '../txApi.js';
import { C, inp, btn, label, SectionLabel, ListItem, Err, Spinner, Badge, useConfirm } from './_shared.jsx';

const ROLES = ['Super Admin', 'Admin', 'Taxonomy Generator'];

// ── Role and client pickers ───────────────────────────────────────────────────
// Shared by the editor and the add form so a user is described the same way whether they
// are being created or amended — one screen to learn, and no second definition to drift.

function RolePicker({ role, onChange }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ ...label(), marginBottom: 8 }}>Module Role</div>
      <div className="o-pills" style={{ marginBottom: 0 }}>
        {ROLES.map(name => (
          <button key={name} className="o-pill-btn" aria-pressed={role === name}
            onClick={() => onChange(name)}>{name}</button>
        ))}
      </div>
    </div>
  );
}

function ClientPicker({ role, clients, clientIds, onToggle }) {
  if (role === 'Super Admin') {
    return (
      <div style={{ marginBottom: 24, padding: '10px 14px', background: C.s2, borderRadius: 8, border: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 12, color: C.muted }}>Super Admins have full access to all clients and system settings.</div>
      </div>
    );
  }
  const active = clients.filter(c => c.is_active);
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ ...label(), marginBottom: 8 }}>
        Client Access
        {role === 'Admin' && <span style={{ color: C.muted, fontWeight: 400, marginLeft: 8, textTransform: 'none', letterSpacing: 0 }}>— can manage taxonomy & data dictionary</span>}
      </div>
      {active.length === 0 && <div style={{ color: C.muted, fontSize: 12 }}>No active clients.</div>}
      {active.map(c => {
        const on = clientIds.includes(c.id);
        return (
          <div key={c.id} onClick={() => onToggle(c.id)} role="checkbox" aria-checked={on} tabIndex={0}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(c.id); } }}
            style={{
              padding: '7px 10px', borderRadius: 12, marginBottom: 4, cursor: 'pointer',
              background: on ? C.brassWash : 'transparent',
              border: `1px solid ${on ? C.brass + '55' : C.border}`,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
            <div style={{ width: 14, height: 14, borderRadius: 4, border: `2px solid ${on ? C.ink : C.border}`, background: on ? C.ink : 'transparent', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {on && <span style={{ color: C.bg, fontSize: 10, lineHeight: 1 }}>✓</span>}
            </div>
            <span style={{ fontSize: 13, color: on ? C.text : C.dim }}>{c.name}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Existing user ─────────────────────────────────────────────────────────────

function UserDetail({ user, clients, onUpdate }) {
  const [role,      setRole]      = useState(user.role_name);
  const [clientIds, setClientIds] = useState(user.client_ids || []);
  const [saving,    setSaving]    = useState(false);
  const [err,       setErr]       = useState(null);
  const [saved,     setSaved]     = useState(false);
  const [confirmUI, ask] = useConfirm();

  useEffect(() => {
    setRole(user.role_name);
    setClientIds(user.client_ids || []);
    setErr(null); setSaved(false);
  }, [user.user_id]);

  const toggleClient = (id) => setClientIds(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id]);

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      await txApi.updateUser(user.user_id, { role, client_ids: clientIds });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
      onUpdate();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const revoke = async () => {
    const okay = await ask({
      title: `Remove ${user.username}'s module access?`,
      body: 'They keep their Orbit account but lose the Taxonomy Generator until access is '
          + 'granted again.',
      confirmLabel: 'Remove access',
      tone: 'danger',
    });
    if (!okay) return;
    try { await txApi.revokeUser(user.user_id); onUpdate(); }
    catch (e) { setErr(e.message); }
  };

  return (
    <div style={{ padding: '0 0 0 24px', flex: 1, overflowY: 'auto' }}>
      <div style={{ marginBottom: 24, borderBottom: `1px solid ${C.border}`, paddingBottom: 16 }}>
        <div style={{ fontSize: 20, fontWeight: 600, color: C.text, marginBottom: 4 }}>{user.username}</div>
        <div style={{ fontSize: 12, color: C.muted }}>{user.full_name || user.email}</div>
      </div>

      <RolePicker role={role} onChange={setRole} />
      <ClientPicker role={role} clients={clients} clientIds={clientIds} onToggle={toggleClient} />

      <Err msg={err} />
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button style={btn('primary', { padding: '8px 20px', fontSize: 13 })} onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save Changes'}
        </button>
        {saved && <span style={{ color: C.success, fontSize: 12 }}>Saved</span>}
        <button style={btn('danger', { padding: '8px 14px', fontSize: 12, marginLeft: 'auto' })} onClick={revoke}>
          Revoke Access
        </button>
      </div>
      {confirmUI}
    </div>
  );
}

// ── Add a user ────────────────────────────────────────────────────────────────
// One form, one save: who they are, their role, and their clients. Splitting the account
// from its access meant a user existed for a beat holding a role but no client, which is
// an account that can sign in and see an empty tool.

function AddUserPanel({ avail, clients, onDone, onCancel }) {
  // Two ways in: an account that already exists on the platform gains this module on top of
  // whatever else it holds, or a new account is created that can reach this module only.
  const [mode,      setMode]      = useState('existing');
  const [userId,    setUserId]    = useState('');
  const [role,      setRole]      = useState('Taxonomy Generator');
  const [clientIds, setClientIds] = useState([]);
  const [fields,    setFields]    = useState({ username: '', email: '', full_name: '', password: '' });
  const [saving,    setSaving]    = useState(false);
  const [err,       setErr]       = useState(null);

  const set = (k, v) => setFields(f => ({ ...f, [k]: v }));
  const toggleClient = (id) => setClientIds(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id]);

  const ready = mode === 'existing'
    ? !!userId
    : !!(fields.username.trim() && fields.email.trim() && fields.password);

  const submit = async () => {
    if (!ready) return;
    setSaving(true); setErr(null);
    try {
      const client_ids = role === 'Super Admin' ? [] : clientIds;
      if (mode === 'existing') {
        await txApi.grantUser({ user_id: userId, role, client_ids });
        onDone(parseInt(userId));
      } else {
        const { user } = await txApi.createUser({ ...fields, role, client_ids });
        onDone(user?.id ?? null);
      }
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ padding: '0 0 0 24px', flex: 1, overflowY: 'auto' }}>
      <div style={{ marginBottom: 24, borderBottom: `1px solid ${C.border}`, paddingBottom: 16 }}>
        <div style={{ fontSize: 20, fontWeight: 600, color: C.text, marginBottom: 10 }}>Add a user</div>
        <div className="o-pills" style={{ marginBottom: 0 }}>
          <button className="o-pill-btn" aria-pressed={mode === 'existing'}
            onClick={() => { setMode('existing'); setErr(null); }}>Existing Orbit account</button>
          <button className="o-pill-btn" aria-pressed={mode === 'new'}
            onClick={() => { setMode('new'); setErr(null); }}>New account</button>
        </div>
      </div>

      {mode === 'existing' ? (
        <div style={{ marginBottom: 20, maxWidth: 420 }}>
          <div style={{ ...label(), marginBottom: 8 }}>Existing Orbit Account</div>
          <select style={inp()} value={userId} onChange={e => setUserId(e.target.value)}>
            <option value="">— Select user —</option>
            {avail.map(u => (
              <option key={u.id} value={u.id}>{u.username}{u.full_name ? ` (${u.full_name})` : ''}</option>
            ))}
          </select>
          <p style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>
            Already existing user in Orbit without Taxonomy generator access.
          </p>
        </div>
      ) : (
        <div style={{ marginBottom: 20, maxWidth: 420 }}>
          <div style={{ ...label(), marginBottom: 8 }}>Account Details</div>
          <input style={inp({ marginBottom: 8 })} placeholder="Username" autoComplete="off"
            value={fields.username} onChange={e => set('username', e.target.value)} />
          <input style={inp({ marginBottom: 8 })} placeholder="Email" type="email" autoComplete="off"
            value={fields.email} onChange={e => set('email', e.target.value)} />
          <input style={inp({ marginBottom: 8 })} placeholder="Full name (optional)" autoComplete="off"
            value={fields.full_name} onChange={e => set('full_name', e.target.value)} />
          <input style={inp()} placeholder="Temporary password" type="password" autoComplete="new-password"
            value={fields.password} onChange={e => set('password', e.target.value)} />
        </div>
      )}

      <RolePicker role={role} onChange={setRole} />
      <ClientPicker role={role} clients={clients} clientIds={clientIds} onToggle={toggleClient} />

      <Err msg={err} />
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        <button style={btn('primary', { padding: '8px 20px', fontSize: 13 })}
          onClick={submit} disabled={saving || !ready}>
          {saving ? 'Saving…' : mode === 'existing' ? 'Add User' : 'Create User'}
        </button>
        <button style={btn('ghost', { padding: '8px 14px', fontSize: 13 })} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

export default function TeamAccessPage() {
  const [users,   setUsers]   = useState([]);
  const [avail,   setAvail]   = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err,     setErr]     = useState(null);
  const [selId,   setSelId]   = useState(null);
  const [adding,  setAdding]  = useState(false);

  const load = async () => {
    try {
      const [u, c] = await Promise.all([txApi.listUsers(), txApi.listClients()]);
      setUsers(u.users);
      setAvail(u.available_users);
      setClients(c.clients);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const added = async (newId) => {
    setAdding(false);
    await load();
    setSelId(newId);
  };

  const selected = users.find(u => String(u.user_id) === String(selId));

  if (loading) return <Spinner />;

  return (
    <div style={{ display: 'flex', gap: 0, height: 'calc(100vh - 140px)' }}>
      {/* Sidebar */}
      <div style={{ width: 210, flexShrink: 0, borderRight: `1px solid ${C.border}`, paddingRight: 16, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <SectionLabel>Users</SectionLabel>
          {/* Always offered: with no ungranted platform accounts left there is still a new
              one to create, and a button that disappears reads as a fault. */}
          <button onClick={() => { setAdding(true); setSelId(null); }} aria-label="Add a user"
            style={btn('primary', { padding: '2px 10px', fontSize: 18, lineHeight: 1 })}>+</button>
        </div>
        <Err msg={err} />

        {!users.length && <p style={{ fontSize: 12, color: C.muted, marginTop: 10 }}>No module users yet.</p>}
        {users.map(u => (
          <ListItem key={u.user_id} active={String(selId) === String(u.user_id)}
            onClick={() => { setSelId(u.user_id); setAdding(false); }}>
            <div>
              <div style={{ marginBottom: 2 }}>{u.username}</div>
              <Badge color={u.role_name === 'Super Admin' ? C.ink : u.role_name === 'Admin' ? C.warn : C.dim}>{u.role_name}</Badge>
            </div>
          </ListItem>
        ))}
      </div>

      {/* Detail */}
      {adding
        ? <AddUserPanel avail={avail} clients={clients} onDone={added} onCancel={() => setAdding(false)} />
        : selected
          ? <UserDetail user={selected} clients={clients} onUpdate={load} />
          : <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', color:C.muted, fontSize:13 }}>
              ← Select or create a user
            </div>
      }
    </div>
  );
}
